/**
 * Surety bonding on proposals — a pass-through line below Installation, sized
 * as a percent of the client's pre-bonding Total Investment. Pure; no firebase.
 */

import type { GlobalCosts, ProposalBondType, ProposalBonding, ProposalBondingLine } from '../types';

export const BOND_TYPE_LABELS: Record<ProposalBondType, string> = {
  performance: 'Performance bond',
  payment: 'Payment bond',
  bid: 'Bid bond',
  maintenance: 'Maintenance / warranty bond',
  other: 'Other bond',
};

export const BOND_TYPE_ORDER: ProposalBondType[] = ['performance', 'payment', 'bid', 'maintenance', 'other'];

export function createEmptyProposalBonding(): ProposalBonding {
  return { enabled: false, lines: [] };
}

export function createBondingLine(type: ProposalBondType = 'performance', percent = 0): ProposalBondingLine {
  const id = `bond-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  return { id, type, percent, enabled: true };
}

function clampPercent(n: unknown): number {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.min(100, v);
}

/** Lines that actually bill: bonding on, line on, percent > 0. */
export function activeBondingLines(bonding: ProposalBonding | null | undefined): ProposalBondingLine[] {
  if (!bonding?.enabled || !Array.isArray(bonding.lines)) return [];
  return bonding.lines.filter((l) => l && l.enabled !== false && clampPercent(l.percent) > 0);
}

/** Sum of enabled line percents (0 when bonding is off or absent). */
export function effectiveBondingPercent(
  globalCosts: Pick<GlobalCosts, 'bonding'> | null | undefined,
): number {
  const sum = activeBondingLines(globalCosts?.bonding).reduce((s, l) => s + clampPercent(l.percent), 0);
  return Math.min(100, Math.round(sum * 1000) / 1000);
}

/** Premium in whole dollars; 0 when nothing to bond. */
export function computeBondingAmount(baseTotal: number, percent: number): number {
  const base = Number(baseTotal) || 0;
  const pct = clampPercent(percent);
  if (base <= 0 || pct <= 0) return 0;
  return Math.round((base * pct) / 100);
}

/** Signed bonding delta for a change order delta (negative on credits). */
export function computeBondingAdjustment(baseDelta: number, percent: number): number {
  const delta = Number(baseDelta) || 0;
  const pct = clampPercent(percent);
  if (delta === 0 || pct <= 0) return 0;
  return Math.round((delta * pct) / 100);
}

export function bondingLineTypeLabel(line: Pick<ProposalBondingLine, 'type' | 'label'>): string {
  if (line.type === 'other') return (line.label || '').trim() || BOND_TYPE_LABELS.other;
  return BOND_TYPE_LABELS[line.type] || BOND_TYPE_LABELS.other;
}

export function formatBondingPercent(percent: number): string {
  const p = clampPercent(percent);
  // Up to three decimals, trailing zeros trimmed: 2 → "2%", 1.25 → "1.25%".
  return `${parseFloat(p.toFixed(3))}%`;
}

/** Short names of the active bonds, e.g. "Performance & Payment". */
export function bondingTypesSummary(bonding: ProposalBonding | null | undefined): string {
  const names = activeBondingLines(bonding).map((l) =>
    bondingLineTypeLabel(l).replace(/\s+bond$/i, '').replace(/\s*\/\s*warranty/i, ''),
  );
  const unique = names.filter((n, i) => names.indexOf(n) === i);
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  return `${unique.slice(0, -1).join(', ')} & ${unique[unique.length - 1]}`;
}

/** Client line label: "Bonding — Performance & Payment (1.5%)". */
export function bondingLineLabel(bonding: ProposalBonding | null | undefined): string {
  const pct = effectiveBondingPercent({ bonding: bonding ?? undefined });
  if (pct <= 0) return 'Bonding';
  const types = bondingTypesSummary(bonding);
  return types ? `Bonding — ${types} (${formatBondingPercent(pct)})` : `Bonding (${formatBondingPercent(pct)})`;
}

/** Sub-copy on the client line. */
export function bondingLineDescription(bonding: ProposalBonding | null | undefined): string {
  const custom = (bonding?.clientDescription || '').trim();
  if (custom) return custom;
  const pct = effectiveBondingPercent({ bonding: bonding ?? undefined });
  return `Surety bond premium, ${formatBondingPercent(pct)} of the contract value`;
}

/**
 * Simple contract clause, inserted under PAYMENTS only when bonding is on.
 * Same text is used on the web terms and the PDF terms page.
 */
export function bondingClause(bonding: ProposalBonding | null | undefined): string | null {
  const pct = effectiveBondingPercent({ bonding: bonding ?? undefined });
  if (pct <= 0) return null;
  const types = bondingTypesSummary(bonding);
  const typeText = types ? ` (${types})` : '';
  return `The Contract price includes surety bond premiums${typeText} equal to ${formatBondingPercent(pct)} of the Contract value, shown as the "Bonding" line in "Your Investment". Contractor will cause the bonds to be issued by its surety upon execution of this Contract and receipt of the deposit, on the surety's standard forms unless Owner furnishes required forms in writing before execution. The bonding premium adjusts proportionally with any approved Change Order, and bond penal sums are increased or decreased by rider to match the adjusted Contract value.`;
}
